import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Eye, Clock, CheckCircle, ShieldMinus, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/common/DataTable';
import { Account } from '@/types';
import { formatDate } from '@/utils';
import {
  useGetContestantQuery,
  useUpdateContestentAssignTeamMutation,
  useUpdateContestentStatusMutation,
  useUpdateContestentTeamMutation,
} from '@/store/api/accountsApi';
import { useState } from 'react';
import { useDebounce } from '@/hooks';
import { useNavigate } from 'react-router-dom';

interface KycStatusBadgeProps {
  isVerified: boolean;
}

function UserActions({ user }: { user: Account }) {
  const [updateContestentStatus, { isLoading }] = useUpdateContestentStatusMutation();

  const handleStatusUpdate = async (status: 'Active' | 'Eliminated') => {
    try {
      await updateContestentStatus({
        accountId: Number(user.id),
        status,
      }).unwrap();
    } catch (err) {
      console.error('Failed to update user status', err);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0" disabled={isLoading}>
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuSeparator />
        {user.status === 'Active' ? (
          <>
            <DropdownMenuItem onClick={() => handleStatusUpdate('Eliminated')}>
              <ShieldMinus className="mr-2 h-4 w-4" />
              Eliminate
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem onClick={() => handleStatusUpdate('Active')}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            Active
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type UserStatus = 'Active' | 'Eliminated';

function StatusBadge({ status }: { status: UserStatus }) {
  const config: Record<
    UserStatus,
    {
      label: string;
      container: string;
      dot: string;
    }
  > = {
    Active: {
      label: 'Active',
      container: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      dot: 'bg-green-400',
    },
    Eliminated: {
      label: 'Eliminated',
      container: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      dot: 'bg-yellow-400',
    },
  };

  const { label, container, dot } = config[status];

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${container}`}
    >
      <span className={`mr-1 h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

export function KycStatusBadge({ isVerified }: KycStatusBadgeProps) {
  if (isVerified) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
        <CheckCircle className="h-3 w-3" />
        KYC Verified
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
      <Clock className="h-3 w-3" />
      KYC Pending
    </span>
  );
}

export function ContestantPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [updateContestentTeam] = useUpdateContestentTeamMutation();
  const [updateContestentAssignTeam] = useUpdateContestentAssignTeamMutation();

  const { data, isLoading, isError } = useGetContestantQuery(
    {
      skip: pageIndex * pageSize,
      take: pageSize,
      search: debouncedSearch,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const handleTeamUpdate = async (teamId: 1 | 2 | 3, userId: number) => {
    try {
      await updateContestentTeam({
        userId: Number(userId),
        teamId,
      }).unwrap();

      console.log('Selected team:', teamId, 'User:', userId);
    } catch (err) {
      console.error('Failed to update team', err);
    }
  };

  const handleTeamAssignUpdate = async (type: 'TeamMember' | 'Raider', userId: number) => {
    try {
      await updateContestentAssignTeam({
        userId: Number(userId),
        type,
      }).unwrap();

      console.log('Selected team:', type, 'User:', userId);
    } catch (err) {
      console.error('Failed to update team', err);
    }
  };
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0);
  };
  const users: Account[] = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const columns: ColumnDef<Account>[] = [
    {
      header: 'Name',
      cell: ({ row }) => {
        const user = row.original;
        const fullName = `${user.firstname} ${user.lastname}`.trim();

        return (
          <div className="flex items-center space-x-3">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium overflow-hidden
    ${
      user?.profileImage
        ? 'border border-primary bg-transparent'
        : 'bg-primary text-primary-foreground'
    }`}
            >
              {user?.profileImage ? (
                <img
                  src={user.profileImage}
                  alt="profile"
                  className="h-full w-full object-cover rounded-full"
                />
              ) : (
                user?.firstname?.charAt(0).toUpperCase()
              )}
            </div>

            <div>
              <div className="font-medium">{fullName}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    // {
    //   header: 'Mobile No.',
    //   cell: ({ row }) => <span className="capitalize">{row.original.mobile}</span>,
    // },

    // {
    //   header: 'Role',
    //   cell: ({ row }) => (
    //     <span className="capitalize">{row.original.role?.toLowerCase() || '—'}</span>
    //   ),
    // },

    {
      header: 'Details',
      cell: ({ row }) => (
        <div
          onClick={() => navigate(`/contestent-detail/${row?.original?.id}`)}
          className="w-full flex items-center justify-start cursor-pointer"
        >
          <Eye className="h-4 w-4" />
        </div>
      ),
    },
    {
      header: 'Status',
      cell: ({ row }) =>
        row.original.status ? (
          <StatusBadge status={row.original.status as 'Active' | 'Eliminated'} />
        ) : (
          <span>—</span>
        ),
    },

    {
      header: 'Created',
      cell: ({ row }) => <span>{formatDate(new Date(row.original.createdAt))}</span>,
    },
    {
      header: 'Team',
      cell: ({ row }) => {
        const user = row.original;
        type TeamType = 1 | 2 | 3;
        const teams: { value: TeamType; label: string; icon: string }[] = [
          { value: 1, label: 'HAMMER', icon: '🟢' },
          { value: 2, label: 'KNIFE', icon: '🟡' },
          { value: 3, label: 'PISTOL', icon: '🔴' },
        ];

        const selectedTeam = teams.find((t) => t.value === user?.teamId);

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-8 px-2 text-sm" disabled={isLoading}>
                {selectedTeam ? `${selectedTeam.icon} ${selectedTeam.label}` : 'Select Team'}
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start" className="w-[150px]">
              <DropdownMenuLabel>Select Team</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {teams.map((team) => (
                <DropdownMenuItem
                  key={team.value}
                  onClick={() => handleTeamUpdate(team.value, user.id)}
                >
                  {team.icon} {team.label}
                  {team.value === user.teamId && '✓'}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },

    {
      header: 'Type',
      cell: ({ row }) => {
        const user = row.original;
        type TeamAssign = 'TeamMember' | 'Raider';
        const teamAssign: { value: TeamAssign; label: string; icon: string }[] = [
          { value: 'TeamMember', label: 'Team Member', icon: '🟢' },
          { value: 'Raider', label: 'Raider', icon: '🟡' },
        ];

        const selectedTeam = teamAssign.find((t) => t.value === user?.type);

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-8 px-2 text-sm" disabled={isLoading}>
                {selectedTeam ? `${selectedTeam.icon} ${selectedTeam.label}` : 'Select Team'}
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start" className="w-[150px]">
              <DropdownMenuLabel>Select Type</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {teamAssign.map((team) => (
                <DropdownMenuItem
                  key={team.value}
                  onClick={() => handleTeamAssignUpdate(team.value, user.id)}
                >
                  {team.icon} {team.label}
                  {team.value === user.type && '✓'}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <UserActions user={row.original} />,
      enableSorting: false,
      enableHiding: false,
    },
  ];

  const handleDelete = (selectedUsers: Account[]) => {
    if (confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
      alert(`${selectedUsers.length} users deleted!`);
    }
  };
  if (isLoading) {
    return <div className="p-6">Loading users...</div>;
  }

  // if (isError) {
  //   return <div className="p-6 text-red-500">Failed to load users</div>;
  // }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap md:items-center flex-col md:flex-row gap-4 md:gap-10 w-full">
          <h2 className="text-xl lg:text-2xl xl:text-3xl font-bold tracking-tight">
            Contestant Listing
          </h2>
        </div>
        <Button onClick={() => navigate(`/create-contestant`)}>Add Contestant</Button>
      </div>

      <DataTable
        data={users}
        columns={columns}
        pageSize={pageSize}
        pageIndex={pageIndex}
        pageCount={Math.ceil(totalCount / pageSize)}
        manualPagination
        onDelete={handleDelete}
        enableFilters={false}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
        onSearchChange={handleSearchChange}
      />
    </div>
  );
}
